import React, { useState } from 'react';
import axios from 'axios';
import { FaUpload, FaDownload, FaCheck, FaTimes, FaFileExcel } from 'react-icons/fa';
import '../styles/ImportFiles.css';

const ImportFiles = ({ onImportSuccess, importType = 'users' }) => {
  const [activeTab, setActiveTab] = useState(importType);
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [importProgress, setImportProgress] = useState({ total: 0, processed: 0, errors: [] });
  const [previewData, setPreviewData] = useState([]);
  const [showPreview, setShowPreview] = useState(false);

  const USER_TEMPLATE = `firstName,lastName,email,role,skills
John,Doe,john@example.com,asset_technician,Electrical
Jane,Smith,jane@example.com,asset_incharge,Mechanical
Bob,Johnson,bob@example.com,asset_user,General`;

  const ASSET_TEMPLATE = `assetName,category,assetType,brand,serialNumber,quantity,department,purchaseDate,conditions
Laptop,Computing,non-consumable,Dell,DL123456,1,IT,2024-01-15,good
Monitor,Computing,non-consumable,LG,LG987654,2,IT,2024-01-15,good
Paper,Supplies,consumable,Generic,NA,100,Admin,2024-01-20,good`;

  const downloadTemplate = (type) => {
    const template = type === 'users' ? USER_TEMPLATE : ASSET_TEMPLATE;
    const element = document.createElement('a');
    element.setAttribute('href', 'data:text/csv;charset=utf-8,' + encodeURIComponent(template));
    element.setAttribute('download', `${type}_template.csv`);
    element.style.display = 'none';
    document.body.appendChild(element);
    element.click();
    document.body.removeChild(element);
  };

  const parseCSV = async (file, fileType) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const csv = e.target.result;
          const lines = csv.split('\n').filter(line => line.trim());
          const headers = lines[0].split(',').map(h => h.trim());
          
          const data = lines.slice(1).map(line => {
            const values = line.split(',').map(v => v.trim());
            const obj = {};
            headers.forEach((header, index) => {
              obj[header] = values[index] || '';
            });
            return obj;
          }).filter(row => Object.values(row).some(val => val)); // Remove empty rows

          resolve(data);
        } catch (err) {
          reject(new Error('Error parsing CSV file: ' + err.message));
        }
      };
      reader.onerror = () => reject(new Error('Error reading file'));
      reader.readAsText(file);
    });
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (!selectedFile) return;

    // Validate file type
    const validTypes = ['text/csv', 'application/vnd.ms-excel', 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'];
    const validExtensions = ['.csv', '.xls', '.xlsx'];
    
    if (!validTypes.includes(selectedFile.type) && !validExtensions.some(ext => selectedFile.name.endsWith(ext))) {
      setError('Please upload a valid CSV or Excel file');
      return;
    }

    setFile(selectedFile);
    setError('');
    setSuccess('');
  };

  const handlePreview = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    try {
      setLoading(true);
      const data = await parseCSV(file, activeTab);
      setPreviewData(data.slice(0, 5)); // Show first 5 rows
      setShowPreview(true);
      setError('');
    } catch (err) {
      setError(err.message);
      setShowPreview(false);
    } finally {
      setLoading(false);
    }
  };

  const handleImport = async () => {
    if (!file) {
      setError('Please select a file first');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setSuccess('');
      setImportProgress({ total: 0, processed: 0, errors: [] });

      const data = await parseCSV(file, activeTab);
      setImportProgress(prev => ({ ...prev, total: data.length }));

      const token = sessionStorage.getItem('token');
      const endpoint = activeTab === 'users' 
        ? `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/admin/import-users`
        : `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:8000'}/api/assets/import-assets`;

      const response = await axios.post(endpoint, { data }, {
        headers: { 
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      const result = response.data;
      setSuccess(`✓ Import completed! ${result.successful} ${activeTab === 'users' ? 'users' : 'assets'} imported successfully.`);
      setImportProgress({
        total: data.length,
        processed: result.successful,
        errors: result.errors || []
      });

      // Reset form
      setFile(null);
      setPreviewData([]);
      setShowPreview(false);
      if (document.querySelector('input[type="file"]')) {
        document.querySelector('input[type="file"]').value = '';
      }

      // Call callback if provided
      if (onImportSuccess) {
        onImportSuccess();
      }

      setTimeout(() => setSuccess(''), 5000);
    } catch (err) {
      const errorMessage = err.response?.data?.message || err.message || 'Error importing file';
      setError(errorMessage);
      console.error('Import error:', err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="import-files-container">
      <div className="import-header">
        <h2>Import {activeTab === 'users' ? 'Users' : 'Assets'}</h2>
        <p>Upload a CSV or Excel file to import multiple {activeTab === 'users' ? 'users' : 'assets'} at once</p>
      </div>

      {/* Tabs */}
      <div className="import-tabs">
        <button 
          className={`tab ${activeTab === 'users' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('users');
            setFile(null);
            setError('');
            setSuccess('');
            setShowPreview(false);
          }}
        >
          Import Users
        </button>
        <button 
          className={`tab ${activeTab === 'assets' ? 'active' : ''}`}
          onClick={() => {
            setActiveTab('assets');
            setFile(null);
            setError('');
            setSuccess('');
            setShowPreview(false);
          }}
        >
          Import Assets
        </button>
      </div>

      {/* Alerts */}
      {error && (
        <div className="alert alert-error">
          <FaTimes /> {error}
        </div>
      )}
      {success && (
        <div className="alert alert-success">
          <FaCheck /> {success}
        </div>
      )}

      <div className="import-content">
        {/* Left Column - Instructions */}
        <div className="import-instructions">
          <h3>
            <FaFileExcel /> File Requirements
          </h3>
          <ul>
            <li>Format: CSV or Excel (.csv, .xls, .xlsx)</li>
            <li>Encoding: UTF-8</li>
            <li>Maximum size: 5 MB</li>
            <li>First row must contain headers</li>
          </ul>

          <h3 style={{ marginTop: '24px' }}>Required Columns</h3>
          {activeTab === 'users' ? (
            <ul>
              <li><strong>firstName</strong> - User's first name</li>
              <li><strong>lastName</strong> - User's last name</li>
              <li><strong>email</strong> - User's email (must be unique)</li>
              <li><strong>role</strong> - asset_technician, asset_incharge, or asset_user</li>
              <li><strong>skills</strong> - Comma-separated skills (optional)</li>
            </ul>
          ) : (
            <ul>
              <li><strong>assetName</strong> - Name of the asset</li>
              <li><strong>category</strong> - Asset category</li>
              <li><strong>assetType</strong> - consumable or non-consumable</li>
              <li><strong>brand</strong> - Brand name</li>
              <li><strong>serialNumber</strong> - Serial number (unique for non-consumables)</li>
              <li><strong>quantity</strong> - Number of units</li>
              <li><strong>department</strong> - Department name</li>
              <li><strong>purchaseDate</strong> - Date (YYYY-MM-DD)</li>
              <li><strong>conditions</strong> - good, fair, or poor</li>
            </ul>
          )}

          <button 
            className="btn-download-template"
            onClick={() => downloadTemplate(activeTab)}
          >
            <FaDownload /> Download Template
          </button>
        </div>

        {/* Right Column - Upload */}
        <div className="import-upload">
          <div className="upload-box">
            <FaUpload className="upload-icon" />
            <h3>Upload File</h3>
            
            <div className="file-input-wrapper">
              <input
                type="file"
                accept=".csv,.xls,.xlsx"
                onChange={handleFileChange}
                disabled={loading}
                id="file-input"
              />
              <label htmlFor="file-input" className="file-label">
                Click to select or drag and drop
              </label>
            </div>

            {file && (
              <div className="file-selected">
                <p><strong>Selected:</strong> {file.name}</p>
                <p><strong>Size:</strong> {(file.size / 1024).toFixed(2)} KB</p>
              </div>
            )}

            <div className="button-group">
              <button
                className="btn btn-primary"
                onClick={handlePreview}
                disabled={!file || loading}
              >
                👁️ Preview Data
              </button>
              <button
                className="btn btn-success"
                onClick={handleImport}
                disabled={!file || loading}
              >
                {loading ? 'Processing...' : `📤 Import ${activeTab === 'users' ? 'Users' : 'Assets'}`}
              </button>
            </div>
          </div>

          {/* Preview */}
          {showPreview && previewData.length > 0 && (
            <div className="preview-box">
              <h3>Preview (First 5 rows)</h3>
              <div className="preview-table">
                <table>
                  <thead>
                    <tr>
                      {Object.keys(previewData[0]).map(key => (
                        <th key={key}>{key}</th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewData.map((row, idx) => (
                      <tr key={idx}>
                        {Object.values(row).map((val, i) => (
                          <td key={i}>{val}</td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Import Progress */}
          {importProgress.total > 0 && (
            <div className="progress-box">
              <h3>Import Results</h3>
              <div className="progress-stats">
                <div className="stat success">
                  <strong>{importProgress.processed}</strong>
                  <p>Successful</p>
                </div>
                <div className="stat error">
                  <strong>{importProgress.errors.length}</strong>
                  <p>Failed</p>
                </div>
              </div>
              
              {importProgress.errors.length > 0 && (
                <div className="errors-list">
                  <h4>Errors:</h4>
                  <ul>
                    {importProgress.errors.slice(0, 5).map((err, idx) => (
                      <li key={idx}>
                        <strong>Row {err.row}:</strong> {err.message}
                      </li>
                    ))}
                    {importProgress.errors.length > 5 && (
                      <li>... and {importProgress.errors.length - 5} more errors</li>
                    )}
                  </ul>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ImportFiles;
